import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { Users, TrendingUp, AlertTriangle, Activity, RefreshCw } from "lucide-react";
import { useUsageMetrics } from "@/hooks/useUsageMetrics";
import { useClients } from "@/hooks/useClients";
import { Skeleton } from "@/components/ui/skeleton";

export const UsageMetricsTab = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const [selectedClient, setSelectedClient] = useState("all");

  // Calcular datas baseadas no período selecionado
  const endDate = new Date().toISOString().split('T')[0];
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - parseInt(selectedPeriod));
  const startDateStr = startDate.toISOString().split('T')[0];

  const {
    usageMetrics,
    licenseLimits,
    isLoading,
    collectCurrentMetrics,
    isCollecting,
    calculateUsagePercentage,
    getChartData
  } = useUsageMetrics({
    clientId: selectedClient,
    startDate: startDateStr,
    endDate: endDate
  });

  const { clients } = useClients();

  const chartData = getChartData();

  // Filtrar métricas mais recentes por cliente para o resumo
  const latestMetricsByClient = usageMetrics.reduce((acc, metric) => {
    const clientId = metric.client_id;
    if (!acc[clientId] || new Date(metric.metric_date) > new Date(acc[clientId].metric_date)) {
      acc[clientId] = metric;
    }
    return acc;
  }, {} as Record<string, typeof usageMetrics[0]>);

  const filteredMetrics = Object.values(latestMetricsByClient).filter(metric =>
    selectedClient === "all" || metric.client_id === selectedClient
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Skeleton para controles */}
        <div className="flex gap-4">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Skeleton para gráficos */}
        <div className="grid gap-6 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Skeleton para cards de clientes */}
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <Skeleton className="h-6 w-32" />
                  <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controles */}
      <div className="flex gap-4 items-center">
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedClient} onValueChange={setSelectedClient}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Cliente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Clientes</SelectItem>
            {clients.map((client) => (
              <SelectItem key={client.id} value={client.id}>
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button 
          variant="outline" 
          onClick={() => collectCurrentMetrics(selectedClient !== 'all' ? selectedClient : undefined)}
          disabled={isCollecting}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isCollecting ? 'animate-spin' : ''}`} />
          {isCollecting ? 'Coletando...' : 'Coletar Métricas'}
        </Button>
      </div>

      {/* Gráficos */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Usuários por Tipo</CardTitle>
            <CardDescription>Distribuição de técnicos e usuários</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="client" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="technicians" fill="#3b82f6" name="Técnicos" />
                <Bar dataKey="users" fill="#10b981" name="Usuários" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tickets por Período</CardTitle>
            <CardDescription>Criados vs Resolvidos</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="tickets_created" 
                  stroke="#f59e0b" 
                  name="Criados"
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="tickets_resolved" 
                  stroke="#10b981" 
                  name="Resolvidos"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Resumo por Cliente */}
      <div className="grid gap-4">
        {filteredMetrics.map((metric) => {
          const clientName = metric.client?.name || 'Cliente Desconhecido';
          const limits = licenseLimits[metric.client_id];
          
          const techUsage = calculateUsagePercentage(
            metric.total_technicians, 
            limits?.max_technicians || null
          );
          const userUsage = calculateUsagePercentage(
            metric.total_users, 
            limits?.max_users || null
          );

          const getUsageStatus = (usage: number) => {
            if (usage >= 100) return { variant: 'destructive' as const, label: 'Limite atingido' };
            if (usage >= 80) return { variant: 'secondary' as const, label: 'Próximo do limite' };
            return { variant: 'default' as const, label: 'Normal' };
          };

          const techStatus = getUsageStatus(techUsage);
          const userStatus = getUsageStatus(userUsage);

          return (
            <Card key={metric.client_id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{clientName}</CardTitle>
                    <CardDescription>
                      Dados de {new Date(metric.metric_date).toLocaleDateString('pt-BR')}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={techStatus.variant}>{techStatus.label}</Badge>
                    {limits?.max_users && (
                      <Badge variant={userStatus.variant}>{userStatus.label}</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Técnicos */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Técnicos
                      </span>
                      <span className="text-sm">
                        {metric.total_technicians} / {limits?.max_technicians || '∞'}
                      </span>
                    </div>
                    {limits?.max_technicians && limits.max_technicians > 0 && (
                      <Progress value={techUsage} className="h-2" />
                    )}
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Ativos: {metric.active_technicians}</span>
                      <span>{techUsage.toFixed(1)}% utilizado</span>
                    </div>
                  </div>

                  {/* Usuários */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        Usuários
                      </span>
                      <span className="text-sm">
                        {metric.total_users} / {limits?.max_users || '∞'}
                      </span>
                    </div>
                    {limits?.max_users && limits.max_users > 0 && (
                      <Progress value={userUsage} className="h-2" />
                    )}
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Ativos: {metric.active_users}</span>
                      <span>{userUsage.toFixed(1)}% utilizado</span>
                    </div>
                  </div>
                </div>

                {/* Métricas de Tickets */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <TrendingUp className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Tickets Criados</p>
                      <p className="text-2xl font-bold">{metric.tickets_created}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Activity className="h-8 w-8 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Tickets Resolvidos</p>
                      <p className="text-2xl font-bold">{metric.tickets_resolved}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        
        {filteredMetrics.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nenhuma métrica encontrada</h3>
              <p className="text-muted-foreground mb-4">
                Não há dados de uso para o período selecionado
              </p>
              <Button 
                onClick={() => collectCurrentMetrics(selectedClient !== 'all' ? selectedClient : undefined)}
                disabled={isCollecting}
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isCollecting ? 'animate-spin' : ''}`} />
                Coletar Métricas Agora
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};